import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TFunction } from 'i18next';

const afterFrames = (count: number, callback: () => void) => {
    requestAnimationFrame(() => (count > 1 ? afterFrames(count - 1, callback) : callback()));
};

// A modal form covers the page, so a hash change (another tab of the page) behind it would leave
// nothing on that tab clickable: treat it as leaving the form. A plain link fires `hashchange`, a
// router navigation (navigate, RouteLink) only updates the location; one change may arrive both ways.
export default function useCloseOnHashChange(hasUnsavedChanges: boolean, onHide: () => void, t: TFunction) {
    const location = useLocation();
    const navigate = useNavigate();
    const openedHash = useRef(window.location.hash);
    const closed = useRef(false);
    const confirming = useRef(false);
    const check = useRef(() => {});

    check.current = () => {
        if (closed.current || confirming.current || window.location.hash == openedHash.current) {
            return;
        }
        if (!hasUnsavedChanges) {
            closed.current = true;
            onHide();
            return;
        }
        // Ask once the new tab has painted, so a refusal reads as going back rather than a flash of the other tab.
        // The page switches its tab in two renders (useHash, then useFormTabs' effect): wait them out.
        confirming.current = true;
        afterFrames(3, () =>
            setTimeout(() => {
                confirming.current = false;
                if (window.location.hash == openedHash.current) {
                    return;
                }
                if (!window.confirm(t('form_close_unsaved_confirm'))) {
                    navigate(
                        { pathname: window.location.pathname, search: window.location.search, hash: openedHash.current },
                        { replace: true },
                    );
                    return;
                }
                closed.current = true;
                onHide();
            }),
        );
    };

    useEffect(() => {
        check.current();
    }, [location.hash]);

    useEffect(() => {
        const onHashChange = () => check.current();
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, []);
}
